import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRecits, type StoryListItemDto } from '@/api/hooks'
import { StoryReader } from '@/components/recits/StoryReader'
import { GrimoireIcon, ChevronDown } from '@/components/game/GameIcons'
import { useCountdown } from '@/hooks/useCountdown'

/**
 * /recits — the player's expedition journal list.
 *
 * Each card shows the story title, island name and the expedition state
 * (En route / À lire / En retour / Terminé). A readable or finished récit opens
 * the full-screen StoryReader; a travelling/returning one shows its ETA and is
 * not openable (the journal is sealed until the fleet accosts).
 *
 * The ?island=NAME query param auto-opens the récit for the matching island
 * (used by the map CTA), but only if it's actually readable/done.
 */
export function Recits() {
  const { data } = useRecits()
  const recits = data?.recits ?? []
  const [openId, setOpenId] = useState<number | null>(null)
  const [showDone, setShowDone] = useState(false)
  const [params, setParams] = useSearchParams()
  const autoOpened = useRef(false)

  // Active récits (à lire / en route / en retour) stay on top; finished ones are
  // tucked behind a collapsible "Terminés" section so the journal doesn't clutter.
  const active = recits.filter((r) => r.state !== 'done')
  const done = recits.filter((r) => r.state === 'done')

  useEffect(() => {
    if (autoOpened.current) return
    const island = params.get('island')
    if (!island || recits.length === 0) return
    const match = recits.find((r) => r.islandName === island)
    if (match && (match.state === 'readable' || match.state === 'done')) {
      setOpenId(match.id)
      autoOpened.current = true
      setParams({}, { replace: true })
    }
  }, [params, recits, setParams])

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-16 space-y-6">
      {/* ── Heading ── */}
      <div className="flex items-center gap-3">
        <GrimoireIcon className="w-7 h-7" />
        <h1 className="font-display text-3xl sm:text-4xl text-sand-300">Récits</h1>
      </div>

      {/* ── Empty state ── */}
      {recits.length === 0 && (
        <div className="py-10 text-center">
          <p className="text-ocean-100/70 max-w-md mx-auto">
            Aucun récit pour l&apos;instant. Envoie une expédition sur une île mystérieuse pour en ouvrir un.
          </p>
        </div>
      )}

      {/* ── Active récit cards ── */}
      {active.length > 0 && (
        <ul className="space-y-3">
          {active.map((r) => (
            <li key={r.id}>
              <RecitCard recit={r} onOpen={setOpenId} />
            </li>
          ))}
        </ul>
      )}

      {/* ── Completed récits, collapsed by default ── */}
      {done.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            aria-expanded={showDone}
            className="flex items-center gap-2 text-sm font-medium text-ocean-200/80 hover:text-sand-300 transition-colors"
          >
            <ChevronDown
              aria-hidden="true"
              className={`w-4 h-4 transition-transform ${showDone ? '' : '-rotate-90'}`}
            />
            Terminés ({done.length})
          </button>
          {showDone && (
            <ul className="space-y-3">
              {done.map((r) => (
                <li key={r.id}>
                  <RecitCard recit={r} onOpen={setOpenId} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Full-screen reader overlay ── */}
      {openId !== null && (
        <StoryReader id={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  )
}

/**
 * One récit row. Readable/done récits are clickable (open the reader);
 * travelling/returning ones show a live ETA and are inert.
 */
function RecitCard({ recit, onOpen }: { recit: StoryListItemDto; onOpen: (id: number) => void }) {
  const traveling = recit.state === 'traveling'
  const returning = recit.state === 'returning'
  const openable = recit.state === 'readable' || recit.state === 'done'
  const eta = useCountdown(traveling ? recit.arrivesAt : returning ? recit.returnsAt : null)

  const chip = (() => {
    switch (recit.state) {
      case 'readable':
        return (
          <span
            className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium border"
            style={{ background: '#C4B5FD1a', color: '#C4B5FD', borderColor: '#C4B5FD66' }}
          >
            À lire
          </span>
        )
      case 'traveling':
        return (
          <span className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-ocean-700/60 text-ocean-100 border border-ocean-500/40">
            En route
          </span>
        )
      case 'returning':
        return (
          <span className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-ocean-700/60 text-ocean-100 border border-ocean-500/40">
            En retour
          </span>
        )
      case 'done':
      default:
        return (
          <span className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-sand-500/20 text-sand-300 border border-sand-500/40">
            Terminé
          </span>
        )
    }
  })()

  const body = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sand-200 truncate">{recit.title}</p>
        <p className="text-[13px] text-ocean-200/80 mt-0.5">{recit.islandName}</p>
        {(traveling || returning) && (
          <p className="text-[11px] text-ocean-300 mt-1">
            {traveling ? 'Flotte en route' : 'Flotte en retour'}
            {' · '}
            <span className="tabular-nums text-sand-300">{eta}</span>
          </p>
        )}
      </div>
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        {chip}
        {recit.state === 'done' && recit.endingLabel && (
          <span className="text-[11px] text-sand-400/80 italic">{recit.endingLabel}</span>
        )}
      </div>
    </div>
  )

  if (!openable) {
    return (
      <div className="w-full rounded-xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/20 to-[#06222b]/35 p-4 sm:p-5 opacity-80">
        {body}
      </div>
    )
  }

  return (
    <button
      type="button"
      aria-label={recit.title}
      onClick={() => onOpen(recit.id)}
      className="w-full text-left rounded-xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/45 p-4 sm:p-5 hover:border-sand-400/40 hover:from-ocean-700/40 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-sand-400"
    >
      {body}
    </button>
  )
}
